import ExportOutlined from '@ant-design/icons/ExportOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Avatar from 'antd/es/avatar';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import List from 'antd/es/list';
import message from 'antd/es/message';
import Space from 'antd/es/space';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { type TeamUser, TeamUserRoleToLabelMap } from '@mai/types';

import CenterSpin from '@components/CenterSpin';
import SectionContainer from '@components/SectionContainer';
import { useTeamUsersQuery } from '@queries/team-users';
import {
  TEAMS_QUERY_KEY,
  updateTeam,
  useTeamBillingQuery,
  useTeamQuery,
} from '@queries/teams';
import { useUsersQuery } from '@queries/users';

const { Text, Title } = Typography;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  margin-top: 1rem;
`;

const SubscriptionCard = styled(Card)`
  margin-bottom: 1rem;
`;

const SubscriptionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SubscriptionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`;

const formatCurrency = (
  amount: number | undefined,
  currency: string | undefined,
) => {
  if (amount === undefined) return 'N/A';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  });
  return formatter.format(amount / 100); // Convert cents to dollars
};

const formatDate = (date: Date | null | undefined) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'green';
    case 'trialing':
      return 'blue';
    case 'past_due':
    case 'unpaid':
      return 'red';
    case 'canceled':
    case 'incomplete_expired':
      return 'gray';
    default:
      return 'default';
  }
};

const ManageTeam = ({ teamId }: { teamId: string }) => {
  const teamQuery = useTeamQuery(teamId);
  const teamBillingQuery = useTeamBillingQuery(teamId);
  const [isEditingName, setIsEditingName] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const teamUsersQuery = useTeamUsersQuery({ teamIds: [teamId] }, !!teamId);

  // Extract all user IDs from team members
  const userIds = useMemo(() => {
    if (!teamUsersQuery.data) return [];
    return teamUsersQuery.data.map((member) => member.userId);
  }, [teamUsersQuery.data]);

  // Fetch user details for all team members
  const usersQuery = useUsersQuery({ userIds }, userIds.length > 0);

  // Create a map of userId to user details for easy lookup
  const userMap = useMemo(() => {
    if (!usersQuery.data) return new Map();
    return new Map(usersQuery.data.map((user) => [user.userId, user]));
  }, [usersQuery.data]);

  const updateTeamMutation = useMutation({
    mutationFn: async (name: string) => {
      return await updateTeam(teamId, { name });
    },
    onSuccess: () => {
      void message.success('Team name updated successfully');
      void queryClient.invalidateQueries({
        queryKey: [TEAMS_QUERY_KEY, teamId],
      });
      setIsEditingName(false);
    },
    onError: (_error) => {
      void message.error('Failed to update team name');
    },
  });

  if (teamQuery.isLoading) {
    return <CenterSpin />;
  }

  if (teamQuery.isError || !teamQuery.data) {
    return <SectionContainer.Error />;
  }

  const handleEditName = () => {
    setIsEditingName(true);
    // We've already checked that teamQuery.data is not null above
    form.setFieldsValue({ name: teamQuery.data?.name || '' });
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    try {
      const values = await form.validateFields();
      updateTeamMutation.mutate(values.name);
    } catch (error) {
      // Handle validation error silently
    }
  };

  const isLoadingMembers = teamUsersQuery.isLoading || usersQuery.isLoading;
  const teamUsers = teamUsersQuery.data || [];

  return (
    <SectionWrapper>
      <Card title="Team Information">
        {isEditingName ? (
          <Form form={form} layout="inline" onFinish={handleSaveName}>
            <Form.Item
              name="name"
              rules={[{ required: true, message: 'Please input team name!' }]}
            >
              <Input
                placeholder="Team name"
                disabled={updateTeamMutation.isPending}
              />
            </Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateTeamMutation.isPending}
              >
                Save
              </Button>
              <Button
                onClick={handleCancelEdit}
                disabled={updateTeamMutation.isPending}
              >
                Cancel
              </Button>
            </Space>
          </Form>
        ) : (
          <Space direction="vertical">
            <Space align="center">
              <Typography.Text>
                <Typography.Text strong>Name:</Typography.Text>{' '}
                {teamQuery.data.name}
              </Typography.Text>

              <Button type="link" onClick={handleEditName}>
                Edit
              </Button>
            </Space>

            <Typography.Text>
              <Typography.Text strong>Team ID:</Typography.Text> {teamId}
            </Typography.Text>
          </Space>
        )}
      </Card>

      <Card title="Team Members">
        {isLoadingMembers ? (
          <CenterSpin />
        ) : teamUsersQuery.isError ? (
          <Text type="danger">Failed to load team members</Text>
        ) : (
          <List
            dataSource={teamUsers}
            renderItem={(item: TeamUser) => {
              const user = userMap.get(item.userId);
              const isAdmin = item.role === 'admin';

              return (
                <List.Item
                  key={item.teamUserId}
                  actions={[
                    isAdmin ? (
                      <Tooltip title="Admin users cannot be edited">
                        <Button key="edit" type="link" disabled>
                          Edit
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button key="edit" type="link">
                        Edit
                      </Button>
                    ),
                    isAdmin ? (
                      <Tooltip title="Admin users cannot be removed">
                        <Button key="remove" type="link" danger disabled>
                          Remove
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button key="remove" type="link" danger>
                        Remove
                      </Button>
                    ),
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={
                      <Space>
                        <Text strong>{user?.fullName || 'Unknown User'}</Text>
                        <Tag color={item.role === 'admin' ? 'blue' : 'green'}>
                          {TeamUserRoleToLabelMap[item.role]}
                        </Tag>
                        {item.isDefault && <Tag color="blue">Default</Tag>}
                      </Space>
                    }
                    description={
                      <Text type="secondary">User ID: {item.userId}</Text>
                    }
                  />
                </List.Item>
              );
            }}
            locale={{ emptyText: 'No team members found' }}
          />
        )}
      </Card>

      <Card title="Billing">
        {teamBillingQuery.isLoading ? (
          <CenterSpin />
        ) : teamBillingQuery.isError ? (
          <Space direction="vertical">
            <Text type="danger">Failed to load billing information</Text>
            <Button type="primary" onClick={() => teamBillingQuery.refetch()}>
              Retry
            </Button>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {teamBillingQuery.data?.subscriptions?.length ? (
              <>
                <Title level={5}>Subscriptions</Title>
                {teamBillingQuery.data.subscriptions.map(
                  (subscription, index) => (
                    <SubscriptionCard key={index}>
                      <SubscriptionHeader>
                        <Space>
                          <Tag color={getStatusColor(subscription.status)}>
                            {subscription.status.toUpperCase()}
                          </Tag>
                          {subscription.cancel_at_period_end && (
                            <Tag color="orange">Cancels at period end</Tag>
                          )}
                        </Space>
                      </SubscriptionHeader>

                      <SubscriptionDetails>
                        {subscription.items.map((item, itemIndex) => (
                          <div key={itemIndex}>
                            <Text strong>
                              {item.price.product_name ||
                                item.price.nickname ||
                                'Subscription'}
                            </Text>

                            <PriceInfo>
                              <Text>
                                {formatCurrency(
                                  item.price.unit_amount,
                                  item.price.currency,
                                )}
                                {item.price.recurring && (
                                  <Text type="secondary">
                                    {' '}
                                    / {item.price.recurring.interval_count ||
                                      1}{' '}
                                    {item.price.recurring.interval || 'month'}
                                  </Text>
                                )}
                              </Text>

                              {item.quantity && item.quantity > 1 && (
                                <Text type="secondary">× {item.quantity}</Text>
                              )}
                            </PriceInfo>
                          </div>
                        ))}

                        <Space
                          direction="vertical"
                          style={{ marginTop: '1rem' }}
                        >
                          <Text type="secondary">
                            Current period:{' '}
                            {formatDate(subscription.current_period_start)} -{' '}
                            {formatDate(subscription.current_period_end)}
                          </Text>

                          {subscription.trial_end && (
                            <Text type="secondary">
                              Trial ends: {formatDate(subscription.trial_end)}
                            </Text>
                          )}
                        </Space>
                      </SubscriptionDetails>
                    </SubscriptionCard>
                  ),
                )}
              </>
            ) : (
              <Text>No active subscriptions found.</Text>
            )}

            <Button
              type="primary"
              href={teamBillingQuery.data?.billingPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              icon={<ExportOutlined />}
            >
              Manage Billing
            </Button>
          </Space>
        )}
      </Card>
    </SectionWrapper>
  );
};

export default ManageTeam;
